import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Leaf } from "lucide-react";
import { api, categoryName } from "@/lib/api";
import { FadeUp, Overline } from "@/components/site/Reveal";
import ProductCard from "@/components/site/ProductCard";

const SAVINGS_CONTEXT = {
  "heat-pumps": "on hot water when you replace an old electric system.",
  "air-conditioners": "on year-round comfort versus older gas or electric heating.",
  "solar-panels": "with a typical 6.6 kW (15-panel) system in Melbourne.",
  batteries: "paired with solar, by using more of your own power.",
};

export default function ProductDetail() {
  const { slug, pid } = useParams();
  const [product, setProduct] = useState(null);
  const [missing, setMissing] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setProduct(null);
    setMissing(false);
    api
      .get(`/products/${pid}`)
      .then((d) => {
        setProduct(d.data);
        return api.get("/products", { params: { category: d.data.category } });
      })
      .then((d) => {
        if (d) setRelated((d.data || []).filter((p) => p.id !== pid).slice(0, 3));
      })
      .catch(() => setMissing(true));
  }, [pid]);

  if (missing) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 px-6 pt-28 text-center" data-testid="product-missing">
        <h1 className="font-display text-3xl font-extrabold text-forest-700">Product not found</h1>
        <Link to={`/products/${slug}`} className="inline-flex items-center gap-2 rounded-full bg-terra px-6 py-3 text-sm font-bold text-bone">
          <ArrowLeft className="h-4 w-4" /> Back to {categoryName(slug)}
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-7xl px-6 pt-32 pb-20" data-testid="product-loading">
        <div className="grid gap-12 lg:grid-cols-2">
          <div className="aspect-[4/5] w-full animate-pulse rounded-lg bg-sage" />
          <div className="space-y-4">
            <div className="h-4 w-24 animate-pulse rounded bg-sage" />
            <div className="h-10 w-3/4 animate-pulse rounded bg-sage" />
            <div className="h-24 w-full animate-pulse rounded bg-sage" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 sm:pt-32" data-testid="product-detail-page">
      <div className="mx-auto max-w-7xl px-6">
        <Link
          to={`/products/${product.category}`}
          data-testid="product-back-link"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-moss transition-colors hover:text-terra"
        >
          <ArrowLeft className="h-4 w-4" /> {categoryName(product.category)}
        </Link>

        <div className="mt-8 grid gap-12 pb-20 lg:grid-cols-2">
          <div className="relative" data-testid="product-image-frame">
            <div className="clip-arch overflow-hidden border border-forest-700/10 bg-sage">
              <img src={product.image} alt={product.name} className="h-[420px] w-full object-cover object-top sm:h-[540px]" />
            </div>
            {product.featured && (
              <span className="absolute left-5 top-5 rounded-full bg-bone/90 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-forest-700 shadow">
                Popular
              </span>
            )}
          </div>

          <div className="lg:pt-4">
            <Overline className="text-terra">{product.brand}</Overline>
            <h1 className="font-display mt-3 text-3xl font-extrabold tracking-tight text-forest-700 sm:text-4xl" data-testid="product-name">
              {product.name}
            </h1>
            {product.tagline && <p className="mt-3 text-lg text-moss">{product.tagline}</p>}
            {product.description && (
              <p className="mt-5 text-base leading-relaxed text-moss">{product.description}</p>
            )}

            {product.features?.length > 0 && (
              <ul className="mt-7 space-y-2.5" data-testid="product-features">
                {product.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-forest-700">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-forest-700/10">
                      <Check className="h-3 w-3 text-forest-700" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            )}

            {product.specs?.length > 0 && (
              <div className="mt-8 overflow-hidden rounded-lg border border-forest-700/10" data-testid="product-specs-table">
                <table className="w-full text-sm">
                  <tbody>
                    {product.specs.map((s, i) => (
                      <tr key={i} className={i % 2 === 0 ? "bg-bone-dark" : "bg-bone"}>
                        <td className="px-4 py-2.5 font-bold text-forest-700">{s.key}</td>
                        <td className="px-4 py-2.5 text-moss">{s.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {product.est_savings > 0 && (
              <div className="mt-8 flex items-start gap-4 rounded-lg border border-leaf/30 bg-forest-700/5 p-5" data-testid="savings-band">
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-leaf/15">
                  <Leaf className="h-5 w-5 text-leaf" />
                </span>
                <div>
                  <p className="text-sm font-bold text-forest-700" data-testid="savings-amount">
                    Save up to ~${product.est_savings.toLocaleString("en-AU")} a year{" "}
                    {SAVINGS_CONTEXT[product.category] || "on your energy bills."}
                  </p>
                  <p className="mt-1 text-xs text-moss/80">
                    Estimate only — based on Victorian average usage and tariffs. Your actual
                    savings depend on your household, the system you're replacing and energy
                    prices. We'll size it properly in your free quote.
                  </p>
                </div>
              </div>
            )}

            <p className="mt-4 text-xs text-moss/80">
              Ask us for current model numbers, warranties and pricing in your quote.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to={`/contact?interest=${encodeURIComponent(product.name)}`}
                data-testid="product-request-quote"
                className="inline-flex items-center gap-2 rounded-full bg-terra px-7 py-4 text-sm font-bold text-bone transition-colors hover:bg-terra-dark"
              >
                Request a quote <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/book-inspection"
                data-testid="product-book-inspection"
                className="inline-flex items-center gap-2 rounded-full border border-forest-700/25 px-7 py-4 text-sm font-bold text-forest-700 transition-colors hover:border-terra hover:text-terra"
              >
                Book a free inspection
              </Link>
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="border-t border-forest-700/10 bg-bone-dark py-20" data-testid="product-related">
          <div className="mx-auto max-w-7xl px-6">
            <h2 className="font-display text-2xl font-extrabold text-forest-700">
              More in {categoryName(product.category)}
            </h2>
            <div className="mt-10 grid gap-x-8 gap-y-14 sm:grid-cols-2 xl:grid-cols-3">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
